import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  LABELS,
  MESSAGES,
  SHOW_PARTICIPANT_PROJECTION,
  VIEW_TYPES,
} from '../utils/constants'
import { getAllVisits } from '../../../../VisitSchedules/routes/VisitSchedulesPage/utils/visitsScheduleUtils'
import {
  scheduledInstrumentCoversStudyDay,
  studyDurationFromListToDays,
} from '../utils/studyTimelineGridUtils'
import { buildInstrumentIdToName } from '../utils/studyTimelineScheduleDisplay'
import { DatePicker } from 'components/UIElements'
import OverviewView from './OverviewView'
import ScheduledView from './ScheduledView'
import TimelineLegend from './TimelineLegend'
import StudyDayDetails from './StudyDayDetails'
import '../stylesheets/StudyTimeline.scss'

const StudyTimelinePage = props => {
  const {
    currentStudy,
    studyConfig,
    studyTimeline,
    studyID,
    studyVersionID,
    setViewType,
    setSelectedArmId,
    setIncludeVisitCycle,
    setUseAbsoluteStartDate,
    setAbsoluteStartDate,
    setFilterByInstrument,
    setSelectedInstrumentId,
    setSelectedDay,
    setGridLoaded,
    setProjectionMode,
    setProjectionParticipantId,
    fetchParticipantVisitsForProjection,
    onEditSchedule,
  } = props

  const {
    viewType = 'overview',
    arms = [],
    selectedArmId = null,
    includeVisitCycle = false,
    useAbsoluteStartDate = false,
    absoluteStartDate = null,
    filterByInstrument = false,
    instruments = [],
    selectedInstrumentId = null,
    days = [],
    scheduledItems = [],
    cycleRanges = [],
    selectedDay = 1,
    gridLoaded = false,
    projectionMode = 'template',
    projectionParticipantId = null,
    studyDurationFromList = null,
  } = studyTimeline || {}

  const studyDurationDays = studyDurationFromListToDays(studyDurationFromList)

  const [selectedDayInput, setSelectedDayInput] = useState(selectedDay || 1)

  // Sync selectedDayInput when selectedDay changes externally
  useEffect(() => {
    setSelectedDayInput(selectedDay || 1)
  }, [selectedDay])

  // Check visit cycling enablement
  const visitSupport = studyConfig?.visit_support
  const isVisitCyclingEnabled = visitSupport?.version > 1
  const isArmsEnabled = visitSupport?.arms_enabled
  const isArmDropdownDisabled = !isVisitCyclingEnabled || !isArmsEnabled

  const handleChangeView = event => {
    setViewType(event.target.value)
  }

  const handleSelectArm = event => {
    setSelectedArmId(event.target.value)
  }

  const handleIncludeVisitCycle = event => {
    setIncludeVisitCycle(event.target.checked)
  }

  const handleUseAbsoluteStartDate = event => {
    const checked = event.target.checked
    setUseAbsoluteStartDate(checked)
    // If unchecking, keep the date but if checking and no date, set to today
    if (checked && !absoluteStartDate) {
      setAbsoluteStartDate(moment().format('YYYY-MM-DD'))
    }
  }

  const handleAbsoluteStartDateChange = (momentDate) => {
    if (momentDate && momentDate.isValid()) {
      setAbsoluteStartDate(momentDate.format('YYYY-MM-DD'))
    } else {
      setAbsoluteStartDate(null)
    }
  }

  // Format date for display: DD MMM YYYY
  const formatDisplayDate = (dateString) => {
    if (!dateString) return ''
    return moment(dateString).format('DD MMM YYYY').toUpperCase()
  }

  // Get moment object for the absolute start date
  const absoluteStartDateMoment = absoluteStartDate ? moment(absoluteStartDate) : null

  const handleFilterByInstrument = event => {
    setFilterByInstrument(event.target.checked)
  }

  const handleSelectedInstrumentChange = event => {
    setSelectedInstrumentId(event.target.value || null)
  }

  const handleSelectDay = day => {
    setSelectedDay(day)
    setSelectedDayInput(day)
  }

  const handleSelectedDayInputChange = event => {
    const day = parseInt(event.target.value, 10)
    if (!isNaN(day) && day > 0) {
      setSelectedDayInput(day)
    }
  }

  const handleGoButtonClick = () => {
    // Apply the selected day and trigger data reload
    if (selectedDayInput && selectedDayInput > 0) {
      setSelectedDay(selectedDayInput)
    }
    // Mark grid as loaded so it will be displayed
    setGridLoaded(true)
  }

  // Check if data is loaded and ready
  // Data is ready when we have arms array and instruments array (they may be empty - 0 arms or 0 instruments is valid)
  // If arms are available (length > 0) and arm dropdown is enabled, we need a selected arm
  // If arms dropdown is disabled (visit cycling not enabled), we can proceed without arm selection
  const hasArmsData = Array.isArray(arms)
  const hasInstrumentsData = Array.isArray(instruments)
  const isDataReady = hasArmsData && hasInstrumentsData
  // Only require arm selection if arms exist AND arm dropdown is enabled
  const needsArmSelection = !isArmDropdownDisabled && arms.length > 0 && !selectedArmId
  const isGoButtonDisabled = !isDataReady || needsArmSelection

  // When "Filter by Instrument" is checked and an instrument is selected, only that instrument shows in grid/scheduled view
  const filteredScheduledItems = React.useMemo(() => {
    if (filterByInstrument && selectedInstrumentId != null) {
      return (scheduledItems || []).filter(
        item =>
          item.type !== 'instrument' ||
          (item.instrumentId != null && String(item.instrumentId) === String(selectedInstrumentId)),
      )
    }
    return scheduledItems || []
  }, [scheduledItems, filterByInstrument, selectedInstrumentId])

  // Get all items for the selected day, grouped by type (uses filtered list so sidebar matches grid)
  const dayItems = React.useMemo(() => {
    const items = filteredScheduledItems.filter(item => {
      if (item.studyDay === selectedDay) return true
      if (item.type === 'instrument') return scheduledInstrumentCoversStudyDay(item, selectedDay)
      return false
    })
    const cycles = items.filter(item => item.type === 'cycle')
    const visits = items.filter(item => item.type === 'visit')
    const instruments = items.filter(item => item.type === 'instrument')
    return { cycles, visits, instruments }
  }, [filteredScheduledItems, selectedDay])

  // Get cycle ranges that include this day
  const dayCycleRanges = React.useMemo(() => {
    return cycleRanges.filter(
      range => selectedDay >= range.startDay && selectedDay <= range.endDay,
    )
  }, [cycleRanges, selectedDay])

  // Map visit ID -> visit name from all arms (so instrument detail shows visit name even when instrument is deployed for another arm's visit)
  const visitIdToName = React.useMemo(() => {
    const map = {}
    const allVisits = getAllVisits(arms || []) || []
    allVisits.forEach(visit => {
      if (visit.id != null) {
        map[String(visit.id)] = visit.name || visit.metadata?.display_name || String(visit.id)
      }
    })
    return map
  }, [arms])

  const instrumentIdToName = React.useMemo(() => buildInstrumentIdToName(instruments), [instruments])

  const scheduleAnchorContext = React.useMemo(
    () => ({
      useAbsoluteStartDate,
      absoluteStartDate,
      visits: getAllVisits(arms || []) || [],
      arms,
      projectionMode,
    }),
    [useAbsoluteStartDate, absoluteStartDate, arms, projectionMode],
  )

  return (
    <div className='study-timeline-page'>
      <div className='study-timeline-header'>
        <div className='study-timeline-controls'>
          <div className='study-timeline-control'>
            <label htmlFor='study-timeline-arm-select'>
              {LABELS.SELECT_ARM}
            </label>
            <select
              id='study-timeline-arm-select'
              value={selectedArmId || ''}
              onChange={handleSelectArm}
              disabled={isArmDropdownDisabled}
            >
              {arms.length === 0 ? (
                <option value=''>{LABELS.NO_ARMS_AVAILABLE}</option>
              ) : (
                arms.map(arm => (
                  <option key={arm.id} value={arm.id}>
                    {arm.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className='study-timeline-control'>
            <label>
              <input
                type='checkbox'
                checked={includeVisitCycle}
                onChange={handleIncludeVisitCycle}
                disabled={!isVisitCyclingEnabled}
              />
              {LABELS.INCLUDE_VISIT_CYCLE}
            </label>
          </div>

          <div className='study-timeline-control study-timeline-absolute-date-control'>
            <div className='study-timeline-absolute-date-row'>
              <label>
                <input
                  type='checkbox'
                  checked={useAbsoluteStartDate}
                  onChange={handleUseAbsoluteStartDate}
                />
                {LABELS.SELECT_START_DATE_ABSOLUTE}
              </label>
              {useAbsoluteStartDate && (
                <div className='study-timeline-date-picker-wrapper'>
                  <DatePicker
                    date={absoluteStartDateMoment}
                    onDayChange={handleAbsoluteStartDateChange}
                    placeholder={LABELS.SELECT_START_DATE}
                    inputClassName='study-timeline-date-input'
                  />
                </div>
              )}
            </div>
            {useAbsoluteStartDate && (
              <p className='study-timeline-help-text'>
                {MESSAGES.ABSOLUTE_DATE_HELP}
              </p>
            )}
          </div>

          {SHOW_PARTICIPANT_PROJECTION && (
            <div className='study-timeline-control study-timeline-participant-projection'>
              <label>
                <input
                  type='checkbox'
                  checked={projectionMode === 'participant'}
                  onChange={event =>
                    setProjectionMode(event.target.checked ? 'participant' : 'template')
                  }
                />
                {LABELS.PARTICIPANT_PROJECTION}
              </label>
              {projectionMode === 'participant' && (
                <div className='study-timeline-participant-id-row'>
                  <input
                    type='text'
                    className='study-timeline-participant-id-input'
                    placeholder={LABELS.PARTICIPANT_ID_PLACEHOLDER}
                    value={projectionParticipantId || ''}
                    onChange={event => setProjectionParticipantId(event.target.value || null)}
                    onBlur={() => {
                      if (projectionParticipantId && studyID) {
                        fetchParticipantVisitsForProjection(studyID, projectionParticipantId)
                      }
                    }}
                  />
                </div>
              )}
              <p className='study-timeline-help-text'>{MESSAGES.PROJECTION_DISCLAIMER}</p>
            </div>
          )}

          <div className='study-timeline-control'>
            <label htmlFor='study-timeline-day-select'>
              {LABELS.SELECT_DAY}
            </label>
            <input
              id='study-timeline-day-select'
              type='number'
              min='1'
              value={selectedDayInput}
              onChange={handleSelectedDayInputChange}
              className='study-timeline-day-input'
            />
          </div>

          <div className='study-timeline-control'>
            <label htmlFor='study-timeline-view-select'>{LABELS.DISPLAY_TYPE}</label>
            <select
              id='study-timeline-view-select'
              value={viewType}
              onChange={handleChangeView}
            >
              <option value={VIEW_TYPES.OVERVIEW}>{LABELS.OVERVIEW}</option>
              <option value={VIEW_TYPES.SCHEDULED}>{LABELS.SCHEDULED}</option>
            </select>
          </div>

          <div className='study-timeline-control'>
            <button
              type='button'
              onClick={handleGoButtonClick}
              className='study-timeline-go-button'
              disabled={isGoButtonDisabled}
            >
              {LABELS.GO}
            </button>
          </div>
        </div>
      </div>

      <div className='study-timeline-content'>
        <div className='study-timeline-main'>
          {gridLoaded ? (
            (days && days.length > 0) || (Array.isArray(days) && days.length === 0) ? (
              viewType === 'scheduled' ? (
                <ScheduledView
                  maxStudyDay={days?.length > 0 ? days[days.length - 1] : null}
                  scheduledItems={filteredScheduledItems}
                  cycleRanges={cycleRanges || []}
                  selectedDay={selectedDay}
                  onSelectDay={handleSelectDay}
                  visitIdToName={visitIdToName}
                  instrumentIdToName={instrumentIdToName}
                  useAbsoluteStartDate={useAbsoluteStartDate}
                  absoluteStartDate={absoluteStartDate}
                />
              ) : (
                <OverviewView
                  days={days && days.length > 0 ? days : Array.from({ length: 28 }, (_, i) => i + 1)}
                  scheduledItems={filteredScheduledItems}
                  cycleRanges={cycleRanges || []}
                  selectedDay={selectedDay}
                  onSelectDay={handleSelectDay}
                  visitIdToName={visitIdToName}
                  instrumentIdToName={instrumentIdToName}
                  useAbsoluteStartDate={useAbsoluteStartDate}
                  absoluteStartDate={absoluteStartDate}
                />
              )
            ) : (
              <div className='study-timeline-empty-state'>
                <p>{MESSAGES.NO_DAYS_DATA}</p>
              </div>
            )
          ) : (
            <div className='study-timeline-empty-state'>
              <p>{MESSAGES.CONFIGURE_AND_GO}</p>
            </div>
          )}
        </div>

        <div className='study-timeline-sidebar'>
          <div className='study-timeline-sidebar-top'>
            <TimelineLegend />
            <div className='study-timeline-filter-instrument'>
            <label>
              <input
                type='checkbox'
                checked={filterByInstrument}
                onChange={handleFilterByInstrument}
              />
              {LABELS.FILTER_BY_INSTRUMENT}
            </label>
            {filterByInstrument && (
              <select
                value={selectedInstrumentId || ''}
                onChange={handleSelectedInstrumentChange}
                className={`study-timeline-instrument-select${selectedInstrumentId ? ' study-timeline-instrument-select--has-selection' : ''}`}
              >
                <option value=''>{LABELS.ALL_INSTRUMENTS}</option>
                {instruments?.map(instrument => (
                  <option key={instrument.id} value={instrument.id}>
                    {instrument.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          </div>
          <div className='study-timeline-sidebar-body'>
            <StudyDayDetails
            selectedDay={selectedDay}
            dayItems={dayItems}
            dayCycleRanges={dayCycleRanges}
            studyID={studyID}
            studyVersionID={studyVersionID}
            visitIdToName={visitIdToName}
            instrumentIdToName={instrumentIdToName}
            useAbsoluteStartDate={useAbsoluteStartDate}
            absoluteStartDate={absoluteStartDate}
            onEditSchedule={onEditSchedule}
            currentStudy={currentStudy}
            studyDurationDays={studyDurationDays}
            scheduleAnchorContext={scheduleAnchorContext}
          />
          </div>
        </div>
      </div>
    </div>
  )
}

StudyTimelinePage.propTypes = {
  currentStudy: PropTypes.object,
  studyConfig: PropTypes.object,
  studyTimeline: PropTypes.object,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setViewType: PropTypes.func,
  setSelectedArmId: PropTypes.func,
  setIncludeVisitCycle: PropTypes.func,
  setUseAbsoluteStartDate: PropTypes.func,
  setAbsoluteStartDate: PropTypes.func,
  setFilterByInstrument: PropTypes.func,
  setSelectedInstrumentId: PropTypes.func,
  setSelectedDay: PropTypes.func,
  setGridLoaded: PropTypes.func,
  onEditSchedule: PropTypes.func,
}

export default StudyTimelinePage

